// Роль-слоты кадров. Режимы, у которых бэк объявил `imageRoles`
// (сейчас — Kling с фичей `first_last_frame`), принимают картинки не пулом, а
// по ролям: «Первый кадр» / «Последний кадр». Каждый слот держит ровно одну
// картинку, порядок слотов = порядок ролей = порядок `inputImageUrls`.
//
// Слот роли открывается только после заполнения предыдущей: пропуск в середине
// сдвинул бы кадры на чужие каналы провайдера (это и был баг `Playground task
// failed` — раскладку угадывали по порядку загрузки).
//
// Отступы вокруг ряда задаёт колонка `PromptInputsRow`, в которой слоты живут
// рядом с элементами: собственный padding развалил бы общую вертикаль колонок.

import { useRef, type ChangeEvent } from "react";
import { ImagePlus, Layers, Plus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";
import { MediaLightbox } from "~/shared/ui/media-lightbox";

import type { ImageInputRole } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

import { AttachmentThumbBody, AttachmentThumbCaption } from "./AttachmentThumb";
import { attachmentThumbFrame } from "./attachment-thumb-frame";
import { SlotColumn } from "./SlotColumn";
import { usePendingLightbox } from "./usePendingLightbox";

export interface FrameRoleSlotsProps {
    roles: readonly ImageInputRole[];
    /** Занятое вложение по роли; отсутствие ключа = слот пуст. */
    itemByRole: Partial<Record<ImageInputRole, PendingUpload>>;
    /** Сколько первых ролей обязательно (по `mode.minInputImages`). */
    requiredCount: number;
    disabled: boolean;
    accept?: string;
    onPickFiles: (role: ImageInputRole, files: File[]) => void;
    onPickFromUploaded: (role: ImageInputRole) => void;
    onPickFromGenerated: (role: ImageInputRole) => void;
    onRemove: (localId: string) => void;
    /**
     * Готовая подсказка «почему пара кадров недоступна»; пусто — канал доступен.
     * Гасит слоты со ВТОРОГО: одиночный первый кадр не требует от исполнителя
     * ничего, требование (`first_last_frame`) появляется вместе с парой.
     */
    pairUnavailableHint?: string;
}

interface SlotProps extends Omit<
    FrameRoleSlotsProps,
    "roles" | "itemByRole" | "requiredCount" | "pairUnavailableHint"
> {
    role: ImageInputRole;
    item: PendingUpload | undefined;
    required: boolean;
    locked: boolean;
    unavailableHint?: string;
    /** Открыть кадр крупно; лента лайтбокса общая на весь ряд ролей. */
    onZoom: (localId: string) => void;
}

function FrameRoleSlot({
    role,
    item,
    required,
    locked,
    unavailableHint,
    disabled,
    accept,
    onPickFiles,
    onPickFromUploaded,
    onPickFromGenerated,
    onRemove,
    onZoom,
}: SlotProps) {
    const { t } = useTranslation("chat");
    const fileInputRef = useRef<HTMLInputElement>(null);

    const title = t(`prompt.imageRoles.${role}`);
    const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        // Слот держит одну картинку — лишние выбранные файлы игнорируем.
        const [first] = Array.from(files);
        if (first) onPickFiles(role, [first]);
        event.target.value = "";
    };

    // Колонка общая с элементами и пуловыми каналами: место под подпись роли
    // резервируется одинаково, иначе плитки соседних каналов встают выше.
    return (
        <SlotColumn
            label={
                <>
                    {title}
                    {required && <span className="ml-0.5 text-amber-400">*</span>}
                </>
            }
            labelTitle={title}
        >
            {item ? (
                <>
                    <div
                        className={cn(attachmentThumbFrame(item.status === "error"), "h-16 w-16")}
                        title={item.status === "error" ? (item.error ?? item.fileName) : item.fileName}
                    >
                        <AttachmentThumbBody
                            item={item}
                            onRemove={onRemove}
                            removeLabel={t("prompt.imageRoleSlots.remove", { role: title })}
                            onZoom={onZoom}
                            zoomLabel={t("prompt.attachmentZoom")}
                        />
                    </div>
                    <AttachmentThumbCaption item={item} />
                </>
            ) : (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept ?? "image/*"}
                        hidden
                        onChange={handleFiles}
                    />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                disabled={disabled || locked || unavailableHint !== undefined}
                                aria-label={t("prompt.imageRoleSlots.add", { role: title })}
                                title={
                                    unavailableHint ??
                                    (locked
                                        ? (t("prompt.imageRoleSlots.previousFirst") ?? undefined)
                                        : undefined)
                                }
                                className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-white/15 bg-zinc-900/60 text-zinc-500 transition-colors hover:border-white/30 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <Plus className="h-5 w-5" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                            <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                                <ImagePlus className="mr-2 h-4 w-4" />
                                {t("prompt.attachMenu.uploadFile")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onPickFromUploaded(role)}>
                                <Layers className="mr-2 h-4 w-4" />
                                {t("prompt.attachMenu.fromUploaded")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onPickFromGenerated(role)}>
                                <Sparkles className="mr-2 h-4 w-4" />
                                {t("prompt.attachMenu.fromGenerated")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            )}
        </SlotColumn>
    );
}

export function FrameRoleSlots({
    roles,
    itemByRole,
    requiredCount,
    pairUnavailableHint,
    ...slotProps
}: FrameRoleSlotsProps) {
    // Лента лайтбокса — заполненные кадры в порядке ролей: листая от первого
    // кадра, попадаешь на последний, а не на чужой канал.
    const filledFrames = roles
        .map((role) => itemByRole[role])
        .filter((item): item is PendingUpload => item !== undefined);
    const lightbox = usePendingLightbox(filledFrames);

    if (roles.length === 0) return null;

    return (
        <div className="flex flex-wrap gap-3">
            {roles.map((role, index) => {
                const previousRole = index > 0 ? roles[index - 1] : undefined;
                return (
                    <FrameRoleSlot
                        key={role}
                        role={role}
                        item={itemByRole[role]}
                        required={index < requiredCount}
                        locked={previousRole !== undefined && !itemByRole[previousRole]}
                        unavailableHint={index > 0 ? pairUnavailableHint : undefined}
                        onZoom={lightbox.openAt}
                        {...slotProps}
                    />
                );
            })}

            <MediaLightbox
                items={lightbox.items}
                initialIndex={lightbox.initialIndex}
                isOpen={lightbox.isOpen}
                onClose={lightbox.close}
            />
        </div>
    );
}
